import React from "react";
import { MapPin, Phone, Clock, Navigation } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { BookingForm } from "@/components/BookingForm";
import { CLINIC, HOURS, getOpenStatus } from "@/data/constants";
import { Button } from "@/components/ui/button";

export default function Contact() {
  const status = getOpenStatus();
  const todayIdx = (new Date().getDay() + 6) % 7;

  return (
    <div className="bg-sage-50 min-h-screen">
      <Header />

      <section className="bg-sage-800 text-sage-50 py-16 grain-texture">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <span className="text-gold-light font-700 text-sm uppercase tracking-wider">
            Get in Touch
          </span>
          <h1 className="font-heading text-5xl sm:text-6xl tracking-tight mt-2">
            Visit Kalpataru Dental Care
          </h1>
          <p className="text-sage-200 mt-3 max-w-xl">
            Find us in the heart of Nagpur. Book your appointment online or call us directly
            for emergencies.
          </p>
          <div className="flex flex-wrap gap-4 mt-6">
            <a href={`tel:${CLINIC.phone}`} data-testid="contact-call-btn">
              <Button className="rounded-full bg-danger hover:bg-danger-dark text-white font-700 h-12 px-6">
                <Phone size={18} className="mr-1" /> Call for Emergency
              </Button>
            </a>
            <a href={CLINIC.mapsLink} target="_blank" rel="noreferrer" data-testid="contact-directions-btn">
              <Button className="rounded-full bg-sage-100 text-sage-900 hover:bg-white font-700 h-12 px-6">
                <Navigation size={18} className="mr-1" /> Get Directions
              </Button>
            </a>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="grid lg:grid-cols-2 gap-8 items-start">
          {/* Column 1: Map + info */}
          <div className="space-y-6" data-testid="contact-map-column">
            <div className="bg-white rounded-2xl border border-sage-200 shadow-sm overflow-hidden">
              <iframe
                title="Kalpataru Dental Care location"
                data-testid="google-map"
                src={CLINIC.mapEmbed}
                width="100%"
                height="360"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
              <div className="p-6">
                <div className="flex items-start gap-3">
                  <MapPin className="text-sage-400 mt-0.5 shrink-0" size={20} />
                  <div>
                    <div className="font-700 text-sage-900">Our Address</div>
                    <p className="text-sage-600 text-sm">{CLINIC.address}</p>
                  </div>
                </div>
                <a href={CLINIC.mapsLink} target="_blank" rel="noreferrer">
                  <Button
                    variant="outline"
                    data-testid="map-directions-btn"
                    className="rounded-full border-sage-300 text-sage-700 hover:bg-sage-50 mt-4 font-700"
                  >
                    <Navigation size={16} className="mr-1" /> Get Directions
                  </Button>
                </a>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-sage-200 shadow-sm p-6">
              <div className="flex items-center gap-2 mb-4">
                <Clock className="text-sage-400" size={20} />
                <h3 className="font-heading text-2xl text-sage-900">Working Hours</h3>
                <span
                  data-testid="contact-status-badge"
                  className={`ml-1 inline-flex items-center gap-1.5 text-[11px] font-700 px-2.5 py-1 rounded-full ${
                    status.open ? "bg-emerald-100 text-emerald-700" : "bg-sage-200 text-sage-600"
                  }`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${status.open ? "bg-emerald-500" : "bg-sage-400"}`} />
                  {status.label}
                </span>
              </div>
              <ul className="space-y-2 text-sm">
                {HOURS.map((h, i) => (
                  <li
                    key={h.day}
                    className={`flex justify-between border-b border-sage-100 pb-2 ${
                      i === todayIdx ? "text-sage-900 font-700" : "text-sage-600"
                    }`}
                  >
                    <span>{h.day}</span>
                    <span>{h.open} – {h.close}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Column 2: Booking form */}
          <div data-testid="contact-form-column">
            <BookingForm />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
